// src/components/SpaceProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";
const AGB_URL = "/agb.pdf";
const IMPRESSUM_URL = "https://www.di-inkasso.de/impressum";

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " und ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function isLegacyManualCompanyId(value = "") {
  return String(value).startsWith("company_manual_");
}

function buildStableCompanyId({ existingCompanyId, legalName }) {
  const cleanedExisting =
    existingCompanyId && !isLegacyManualCompanyId(existingCompanyId)
      ? String(existingCompanyId).trim()
      : "";

  if (cleanedExisting) {
    return cleanedExisting;
  }

  const legalSlug = slugify(legalName);
  return legalSlug || "";
}

export default function SpaceProfile({ spaceId: propSpaceId, onSaved }) {
  const navigate = useNavigate();

  const detectedSpaceId =
    propSpaceId ||
    localStorage.getItem("space_id") ||
    localStorage.getItem("SpaceID") ||
    localStorage.getItem("provider_context_id") ||
    "";

  const providerType =
    localStorage.getItem("provider_type") ||
    (detectedSpaceId ? "cobot" : "");

  const isCobotFlow = Boolean(detectedSpaceId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    space_name: localStorage.getItem("space_name") || "",
    legal_name: localStorage.getItem("company_name") || "",
    legal_form: "",
    address_street: "",
    address_zip: "",
    address_city: "",
    address_country: "DE",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    iban: "",
    bic: "",
    vat_id: "",
    inkasso_agb_accepted: false,
    password: "",
    password_confirm: "",
  });

  const stableCompanyId = useMemo(() => {
    return buildStableCompanyId({
      existingCompanyId: localStorage.getItem("company_id"),
      legalName: form.legal_name,
    });
  }, [form.legal_name]);

  const hasExistingCompanyContext = Boolean(
    localStorage.getItem("company_id") &&
      !isLegacyManualCompanyId(localStorage.getItem("company_id"))
  );

  useEffect(() => {
    const existingCompanyId = localStorage.getItem("company_id") || "";
    if (isLegacyManualCompanyId(existingCompanyId)) {
      localStorage.removeItem("company_id");
      localStorage.removeItem("company_context");
      localStorage.removeItem("company_profile_completed");
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");

      if (!detectedSpaceId) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `${API_BASE}/Cobot/Space?space_id=${encodeURIComponent(detectedSpaceId)}`
        );

        const data = await res.json();

        if (!mounted) return;

        const space = data?.space;

        if (space) {
          setForm((prev) => ({
            ...prev,
            ...space,
            space_name:
              space.space_name ||
              prev.space_name ||
              localStorage.getItem("space_name") ||
              "",
            legal_name:
              space.legal_name ||
              space.company_name ||
              prev.legal_name ||
              localStorage.getItem("company_name") ||
              "",
            inkasso_agb_accepted: space.inkasso_agb_accepted === true,
            address_country: space.address_country || "DE",
            password: "",
            password_confirm: "",
          }));

          if (space.space_name) {
            localStorage.setItem("space_name", space.space_name);
          }

          if (space.legal_name || space.company_name) {
            localStorage.setItem(
              "company_name",
              space.legal_name || space.company_name
            );
          }
        }
      } catch (e) {
        if (mounted) {
          setError("Profil konnte nicht geladen werden.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [detectedSpaceId]);

  useEffect(() => {
    if (form.legal_name?.trim()) {
      localStorage.setItem("company_name", form.legal_name.trim());
    }
  }, [form.legal_name]);

  useEffect(() => {
    if (form.space_name?.trim()) {
      localStorage.setItem("space_name", form.space_name.trim());
    }
  }, [form.space_name]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!form.legal_name.trim()) return "Bitte Firmennamen angeben.";
    if (!form.address_street.trim()) return "Bitte Straße angeben.";
    if (!form.address_zip.trim()) return "Bitte PLZ angeben.";
    if (!form.address_city.trim()) return "Bitte Stadt angeben.";
    if (!form.contact_email.trim()) return "Bitte E-Mail angeben.";
    if (!form.iban.trim()) return "Bitte IBAN angeben.";

    if (!form.inkasso_agb_accepted) {
      return "Bitte Inkasso-AGB akzeptieren, um fortzufahren.";
    }

    const passwordFilled = String(form.password || "").trim().length > 0;
    const passwordConfirmFilled =
      String(form.password_confirm || "").trim().length > 0;

    if (!hasExistingCompanyContext && !passwordFilled) {
      return "Bitte ein Passwort für den Firmenzugang festlegen.";
    }

    if (passwordFilled || passwordConfirmFilled) {
      if (String(form.password).length < 8) {
        return "Das Passwort muss mindestens 8 Zeichen lang sein.";
      }

      if (form.password !== form.password_confirm) {
        return "Passwort und Passwort-Wiederholung stimmen nicht überein.";
      }
    }

    return "";
  };

  const persistCompanyContext = (savedProfile = null) => {
    const backendCompanyId =
      savedProfile?.company_id ||
      localStorage.getItem("company_id") ||
      "";

    const companyId = buildStableCompanyId({
      existingCompanyId: backendCompanyId,
      legalName:
        savedProfile?.legal_name ||
        savedProfile?.company_name ||
        form.legal_name,
    });

    if (!companyId) {
      throw new Error("Keine stabile company_id ableitbar.");
    }

    localStorage.setItem("company_id", companyId);
    localStorage.setItem("company_profile_completed", "true");

    const finalCompanyName =
      savedProfile?.legal_name ||
      savedProfile?.company_name ||
      form.legal_name ||
      localStorage.getItem("company_name") ||
      "";

    if (finalCompanyName) {
      localStorage.setItem("company_name", finalCompanyName);
    }

    if (savedProfile?.space_name || form.space_name) {
      localStorage.setItem(
        "space_name",
        savedProfile?.space_name || form.space_name
      );
    }

    if (providerType) {
      localStorage.setItem("provider_type", providerType);
    }

    if (detectedSpaceId) {
      localStorage.setItem("provider_context_id", detectedSpaceId);
      localStorage.setItem("space_id", detectedSpaceId);
      localStorage.setItem("SpaceID", detectedSpaceId);
    }

    const summary = {
      company_id: companyId,
      provider_type: providerType || null,
      provider_context_id: detectedSpaceId || null,
      legal_name: finalCompanyName,
      company_name: finalCompanyName,
      contact_email: savedProfile?.contact_email || form.contact_email || "",
      completed: true,
    };

    localStorage.setItem("company_context", JSON.stringify(summary));
  };

  const saveProfile = async () => {
    setError("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return false;
    }

    setSaving(true);

    try {
      
      const endpoint = `${API_BASE}/company`;

      const passwordValue = String(form.password || "").trim();

      const payload = isCobotFlow
        ? {
            space_id: detectedSpaceId,
            company_id: stableCompanyId || undefined,
            provider_type: providerType || "cobot",
            ...form,
            password: passwordValue || undefined,
            password_confirm: undefined,
          }
        : {
            company_id: stableCompanyId || undefined,
            company_name: form.legal_name || "",
            legal_form: form.legal_form || "",
            address_street: form.address_street || "",
            address_zip: form.address_zip || "",
            address_city: form.address_city || "",
            address_country: form.address_country || "DE",
            contact_person: form.contact_person || "",
            contact_email: form.contact_email || "",
            contact_phone: form.contact_phone || "",
            iban: form.iban || "",
            bic: form.bic || "",
            vat_id: form.vat_id || "",
            inkasso_agb_accepted: form.inkasso_agb_accepted === true,
            password: passwordValue || undefined,
          };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data?.ok !== true) {
        setError(data?.error || "Fehler beim Speichern.");
        return false;
      }

      const savedProfile = data.space || data.company || null;

      persistCompanyContext(savedProfile);

      const finalCompanyId =
        savedProfile?.company_id ||
        localStorage.getItem("company_id") ||
        stableCompanyId ||
        "";

      if (isCobotFlow && detectedSpaceId && finalCompanyId) {
        try {
          await fetch(`${API_BASE}/provider_connection`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: finalCompanyId,
              provider: "cobot",
              external_id: detectedSpaceId,
              secret_name: "stored_in_cobot_tokens",
              metadata: {
                source: "space_profile",
                space_id: detectedSpaceId,
              },
            }),
          });
        } catch (connectionErr) {
          console.error(
            "Cobot provider_connection konnte nicht gespeichert werden",
            connectionErr
          );
        }
      }

      setForm((prev) => ({
        ...prev,
        password: "",
        password_confirm: "",
      }));

      onSaved?.(savedProfile);

      const returnTo =
        localStorage.getItem("return_to") || "/company-dashboard";
      localStorage.removeItem("return_to");
      localStorage.removeItem("connect_mode");

      navigate(returnTo, { replace: true });
      return true;
    } catch (e) {
      console.error("saveProfile Fehler", e);
      setError("Fehler beim Speichern.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    await saveProfile();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b p-4 flex items-center gap-4">
        <img src="/logo.png" alt="DI Inkasso" className="h-10" />
        <div>
          <div className="text-lg font-semibold">Inkasso Plattform</div>
          <div className="text-sm text-gray-500">Firmenprofil</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-8 bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">
              Bitte vervollständigen Sie Ihr Firmenprofil
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Diese Angaben werden für die Inkasso-Übergabe und die Verarbeitung
              Ihrer Forderungen benötigt.
            </p>
          </div>

          <div className="text-xs text-gray-400 text-right">
            {detectedSpaceId ? (
              <>
                <div>Provider-Kontext</div>
                <div className="font-mono text-gray-600">{detectedSpaceId}</div>
              </>
            ) : (
              <div>Manueller Company-Flow</div>
            )}
          </div>
        </div>

        {stableCompanyId ? (
          <div className="mt-4 p-3 rounded bg-blue-50 text-blue-800 text-sm border border-blue-100">
            <div className="font-medium">Company-Kontext</div>
            <div className="mt-1 font-mono break-all">{stableCompanyId}</div>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-6 text-sm text-gray-500">Lade Profil…</div>
        ) : (
          <>
            {error ? (
              <div className="mt-4 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-100">
                {error}
              </div>
            ) : null}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Unternehmens- oder Workspace-Name (optional)
                </label>
                <input
                  name="space_name"
                  value={form.space_name || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. Musterspace CoWorking"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Firma / Auftraggeber *
                </label>
                <input
                  name="legal_name"
                  value={form.legal_name || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. Muster GmbH"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Rechtsform (optional)
                </label>
                <input
                  name="legal_form"
                  value={form.legal_form || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. GmbH"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  USt-ID (optional)
                </label>
                <input
                  name="vat_id"
                  value={form.vat_id || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. DE123456789"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Straße + Nr *
                </label>
                <input
                  name="address_street"
                  value={form.address_street || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. Fischerstraße 1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">PLZ *</label>
                <input
                  name="address_zip"
                  value={form.address_zip || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. 12345"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Stadt *
                </label>
                <input
                  name="address_city"
                  value={form.address_city || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. Platzort"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Land *
                </label>
                <input
                  name="address_country"
                  value={form.address_country || "DE"}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="DE"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Ansprechpartner (optional)
                </label>
                <input
                  name="contact_person"
                  value={form.contact_person || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. Heike"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  E-Mail *
                </label>
                <input
                  name="contact_email"
                  value={form.contact_email || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. info@unternehmen.de"
                  type="email"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Telefon (optional)
                </label>
                <input
                  name="contact_phone"
                  value={form.contact_phone || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="+49 …"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  IBAN *
                </label>
                <input
                  name="iban"
                  value={form.iban || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="DE…"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  BIC (optional)
                </label>
                <input
                  name="bic"
                  value={form.bic || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="z. B. ABCDDEFFXXX"
                />
              </div>

              <div className="md:col-span-2 mt-2 border-t pt-4">
                <div className="text-sm font-semibold text-gray-800">
                  Zugangsdaten
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Für neue Firmen ist ein Passwort erforderlich. Bei bestehenden
                  Firmen kann hier optional ein neues Passwort gesetzt werden.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Passwort {!hasExistingCompanyContext ? "*" : "(optional)"}
                </label>
                <input
                  name="password"
                  type="password"
                  value={form.password || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="Mindestens 8 Zeichen"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Passwort wiederholen {!hasExistingCompanyContext ? "*" : "(optional)"}
                </label>
                <input
                  name="password_confirm"
                  type="password"
                  value={form.password_confirm || ""}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded p-2"
                  placeholder="Passwort erneut eingeben"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="inkasso_agb_accepted"
                  checked={form.inkasso_agb_accepted === true}
                  onChange={handleChange}
                  className="mt-1"
                />
                <div className="text-sm text-gray-700">
                  <div>
                    Ich akzeptiere die{" "}
                    <a
                      href={AGB_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Inkasso-AGB
                    </a>
                    .
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Für die Übergabe an Inkasso ist die Zustimmung erforderlich.
                  </div>
                </div>
              </label>

              <div className="text-xs text-gray-500">
                <a
                  href={IMPRESSUM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  Impressum
                </a>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSaveAndContinue}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Speichern…" : "Speichern und zum Company Dashboard"}
              </button>
            </div>

            <div className="mt-8 text-center text-xs text-gray-400">
              DI Direkt Inkasso ·{" "}
              <a
                href={IMPRESSUM_URL}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                Impressum
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}